import { useEffect, useMemo, useState } from 'react'
import {
    Checkbox,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material'

import { useAtom, useAtomValue } from 'jotai'
import { genealogyAtom, genealogyErrorAtom, genealogyIsLoadingAtom, TGenealogyResponse } from '@/core/store/genealogy.store'
import { distributorRecordsAtom } from '@/core/store/distributor.store'
import { getGenealogy } from '@/core/service/service'
import { base64Encode, getFullRank } from '@/core/utils/utils'
import { CUSTOMER_INFORMATION_KEY } from '@/core/constants/auth'

const GenealogyLayout = () => {
    const [genealogy, setGenealogy] = useAtom(genealogyAtom)
    const [isLoading, setIsLoading] = useAtom(genealogyIsLoadingAtom)
    const [errorMessage, setErrorMessage] = useAtom(genealogyErrorAtom)
    const [selectedLevel, setSelectedLevel] = useState('1')

    const distributorRecords = useAtomValue(distributorRecordsAtom)

    const currentBaid = distributorRecords[0]?.baid ?? ''
    const currentName = distributorRecords[0]?.name ?? ''
    const cstoken = JSON.parse(sessionStorage.getItem(CUSTOMER_INFORMATION_KEY) ?? '{}').token ?? ''
    const customerHref = JSON.parse(sessionStorage.getItem(CUSTOMER_INFORMATION_KEY) ?? '{}').href ?? ''

    const encodedHref = useMemo(() => (customerHref ? base64Encode(customerHref) : ''), [customerHref])

    const rows = useMemo(() => {
        // TODO: map real data when API wired
        if (Array.isArray(genealogy?.items) && genealogy.items.length > 0) {
            return genealogy.items.map(item => ({
                treeLevel: item.treeDepth,
                fullName: item.customer.humanName.fullName,
                baid: item.customer.id.unicity,
                treeRange: item.customer.status === 'Suspended' ? 'Đang tạm đình chỉ hoạt động' : getFullRank(item.customer.cumulativeMetricsProfile.highestRankShort),
                isVip: item.customer.achievementHistory.items[0]?.value?.[0]?.qualified === true,
                metricsHistory: item.customer.metricsProfileHistory,
                status: item.customer.status
            }))
        }
        return []
    }, [genealogy])

    const historyMonths = useMemo(() => {
        const items = rows.map(item => Array.isArray(item.metricsHistory?.items) ? item.metricsHistory?.items : [])
        return Array.from(new Set(items.flatMap(item => item?.map(i => i.period)))).slice(0, 3)
    }, [rows])

    const fallbackMonths = Array.from(historyMonths)

    const metricKeys = ['pv', 'tv', 'ov'] as const

    const handleLevelChange = (event: SelectChangeEvent<string>) => {
        setSelectedLevel(event.target.value)
    }

    const fetchGenealogy = async () => {
        if (!cstoken || !encodedHref || !currentBaid || !currentName) return

        setIsLoading(true)

        try {
            const response = await getGenealogy({
                href: encodedHref,
                maxTreeDepth: selectedLevel,
                token: cstoken
            })

            const data = response.data as TGenealogyResponse

            console.log('data', data)
            if (data) {
                setGenealogy(data)
            } else {
                setErrorMessage('Không có dữ liệu phả hệ')
            }
        } catch (error) {
            console.error('Error fetching genealogy:', error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchGenealogy()
    }, [selectedLevel, cstoken, encodedHref, currentBaid, currentName])

    return (
        <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: '0px 8px 24px rgba(15,15,15,0.06)' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} mb={3}>
                <Typography variant="h6" fontWeight={700} color="text.primary">
                    Phả hệ ({currentBaid}, {currentName})
                </Typography>
                
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>chọn tầng</InputLabel>
                    <Select label="chọn tầng" value={selectedLevel} onChange={handleLevelChange}>
                        {Array.from({ length: 12 }, (_, index) => (
                            <MenuItem key={index} value={String(index + 1)}>
                                Tầng #{index + 1}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                <Table
                    sx={{
                        '& .MuiTableCell-root': {
                            borderBottom: 'none',
                            textAlign: 'center',
                        }
                    }}
                    size="medium">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#111827' }}>
                            <TableCell rowSpan={3} sx={{ color: '#fff', fontWeight: 700 }}>Tầng</TableCell>
                            <TableCell rowSpan={2} sx={{ color: '#fff', fontWeight: 700, textAlign: 'left' }}>Tên</TableCell>
                            <TableCell rowSpan={2} sx={{ color: '#fff', fontWeight: 700 }}>Mã số</TableCell>
                            <TableCell rowSpan={2} sx={{ color: '#fff', fontWeight: 700, textAlign: 'left' }}>Danh hiệu</TableCell>
                            <TableCell rowSpan={2} sx={{ color: '#fff', fontWeight: 700, padding: '0px 5px' }}>VIP</TableCell>
                            {historyMonths.map(item => {
                                return (
                                    <TableCell key={`${item}-group`} colSpan={3} sx={{ color: '#fff', fontWeight: 700 }}>
                                        {item}
                                    </TableCell>
                                )
                            })}
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#1f2937' }}>
                            {fallbackMonths.flatMap(month => (
                                metricKeys.map(metric => (
                                    <TableCell key={`${month}-${metric}`} sx={{ color: '#fff', fontWeight: 700 }}>
                                        {metric}
                                    </TableCell>
                                ))
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody
                        sx={{
                            '& .MuiTableCell-root': {
                                padding: '10px 5px'
                            }
                        }}
                    >
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={5 + fallbackMonths.length * 3} sx={{ py: 6, textAlign: 'center' }}>
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        ) : errorMessage ? (
                            <TableRow>
                                <TableCell colSpan={5 + fallbackMonths.length * 3} sx={{ py: 6, color: 'error.main', fontWeight: 600 }}>
                                    {errorMessage}
                                </TableCell>
                            </TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5 + fallbackMonths.length * 3} sx={{ py: 6, color: 'text.secondary' }}>
                                    Không có dữ liệu
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row, index) => {
                                const metricsHistory = Array.isArray(row.metricsHistory?.items) ? row.metricsHistory?.items.slice(0, 3) : []
                                return (
                                        <TableRow key={`${row.baid}-${index}`} sx={{ backgroundColor: index % 2 === 0 ? 'rgba(0,0,0,0.04)' : '#FFF' }}>
                                            <TableCell sx={{ padding: '5px 0px' }}>{row.treeLevel}</TableCell>
                                            <TableCell sx={{ textAlign: 'center !important' }}>{row.fullName}</TableCell>
                                            <TableCell sx={{ textAlign: 'center !important' }}>{row.baid}</TableCell>
                                            <TableCell sx={{ textAlign: 'center !important', color: row.status === 'Suspended' ? 'error.main' : 'inherit' }}>{row.treeRange}</TableCell>
                                            
                                            <TableCell 
                                                sx={{ 
                                                    padding: '5px 0px !important', 
                                                    borderLeft: '1px solid #e1e1e1',
                                                    borderRight: '1px solid #e1e1e1'
                                                }}>
                                                {row.isVip ? <Checkbox checked={row.isVip} disableRipple sx={{ pointerEvents: 'none' }} /> : null}
                                            </TableCell>

                                            {metricsHistory.map((item, index) => (
                                                metricKeys.map((metric, metricIndex) => (
                                                    <TableCell 
                                                        key={`${item.period}-${metric}-${index}`} 
                                                        sx={{ 
                                                            padding: '0px 5px', 
                                                            borderBottom: '1px solid #e1e1e1',
                                                            borderRight: metricIndex === metricKeys.length - 1 ? '1px solid #e1e1e1' : 'none',
                                                        }}>
                                                        {item?.value?.[metric] ?? '-'}
                                                    </TableCell>
                                                ))
                                            ))}
                                        </TableRow>
                                )
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    )
}

export default GenealogyLayout
